import { white, grey } from '@pelotoncycle/design-system';
import React from 'react';
import ReactModal from 'react-modal';
import { createGlobalStyle } from 'styled-components';
import { ieHideScroll, media } from '@peloton/styles';

export const TIMEOUT_DURATION = 500;

export type CloseHandlers = {
  handleRequestClose: () => void;
};

export type Props = CloseHandlers & {
  children: JSX.Element;
  isOpen: boolean;
  backgroundColor: string;
  ariaLabel: string;
};

// http://reactcommunity.org/react-modal/accessibility/#app-element
if (typeof window !== 'undefined') {
  const root = document.getElementById('__next') ?? document.getElementById('root');
  ReactModal.setAppElement(root as HTMLElement);
}

const FilterFlyout = ({
  children,
  isOpen,
  backgroundColor,
  ariaLabel,
  handleRequestClose,
}: Props) => {
  return (
    <ReactModal
      className="flyout-content"
      overlayClassName="flyout-overlay"
      isOpen={isOpen}
      closeTimeoutMS={TIMEOUT_DURATION}
      contentLabel={ariaLabel}
      onRequestClose={handleRequestClose}
    >
      <GlobalModalStyles backgroundColor={backgroundColor} />
      {children}
    </ReactModal>
  );
};

const GlobalModalStyles = createGlobalStyle<{
  backgroundColor: string;
}>`
    .flyout-content {
      position: fixed;
      background: ${white};
      -webkit-overflow-scrolling: touch;
      outline: none;
      right: 0;
      left: 0;
      bottom: 0;
      border: none;
      height: 70%;
      transform: translate3d(0, 100%, 0);
      transition: 400ms ease;
      z-index: 90005;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      border-radius: 12px;

      ${ieHideScroll}
  
      ${media.tablet`
        transition: 400ms ease;
      `}
  
    }
    
    .ReactModal__Content--after-open {
      transform: translate3d(0, 0, 0);
      
        div nav ul li.nav-panel-item button,
        div nav ul li.nav-panel-item a {
          display: flex;
          margin: 1.25rem 0;
          height: unset;
          font-size: 18px;
          padding: unset;
          justify-content: flex-start;
        }
        div nav ul li a {
          &:after {
            content: none;
          }
        }
  
        div nav ul li.nav-panel-item {
          opacity: 0;
        }
    }
  
    .ReactModal__Content--before-close {
      transform: translate3d(0, 100%, 0);
    }
  
    .flyout-overlay {
      &::before {
        content: "";
  
        /*
          safari specific hack for animating pseudoelements
          see https://stackoverflow.com/questions/16348489/is-there-a-css-hack-for-safari-only-not-chrome
        */
        @media not all and (min-resolution:.001dpcm) {
          @media {
            content: none;
          }
        }
  
        position: fixed;
        z-index: 90005;
        width: 100%;
        height: 100%;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: ${grey[80]};
  
        transition: opacity 500ms ease;
        opacity: 0;
  
        ${media.tablet`
          transition: opacity 600ms ease;
        `}
      }
    }
  
    .ReactModal__Overlay--after-open{
      &::before {
        opacity: 0.85;
  
        /*
          safari specific hack for animating pseudoelements
          see https://stackoverflow.com/questions/16348489/is-there-a-css-hack-for-safari-only-not-chrome
        */
        @media not all and (min-resolution:.001dpcm) {
          @media {
            content: "";
          }
        }
      }
    }
  
    .ReactModal__Overlay--before-close{
      &::before {
        opacity: 0;
      }
    }
  
    .ReactModal__Body--open {
      overflow-y: hidden;
    }
`;

export default FilterFlyout;
